<script setup lang="ts">
import {computed, ref} from 'vue';
import {QMenu} from 'quasar';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';
import type {JournalFeelingOption} from '@/ts/types/component/today-page';
import FeelingsPicker from '@/vue/molecules/feelings-picker.vue';

/**
 * The entry modal's feelings row, as the design system draws it (IPD-3107).
 *
 * One strip under the note holding the feelings chosen so far and, always, the button that opens
 * the rest. Collapsed rather than always open, because a catalogue of a few hundred words is not
 * what the modal should lead with — the mood and the note are, and most entries never touch this.
 *
 * The button stays put once feelings are chosen, changing only its wording from "Add feeling" to
 * "Add more": pills alone left nothing to say a second one could be added, and a row that is
 * entirely a click target does not look like one.
 *
 * The popover holds {@link FeelingsPicker}, the same component the Today tile shows inline, so the
 * two offer the same search, the same grouping and the same pills. Only the framing differs, which
 * is what each design asks for: a step of a mask there, a field of a form here.
 */
defineProps<{
  /** Secondary feelings keyed by mood, so the suggestions relate to the mood on screen. */
  feelingsByMood?: Record<string, JournalFeelingOption[]>;
  fallbackFeelings?: JournalFeelingOption[];
  mood?: MoodDTO | null;
}>();

const selected = defineModel<JournalFeelingOption[]>({default: () => []});
/** Held by the caller, so closing the whole modal drops the query with the rest of the draft. */
const search = defineModel<string>('search', {default: ''});

const isOpen = ref(false);

const addLabel = computed(() => (selected.value.length === 0
  ? 'journal_add_feeling'
  : 'journal_add_more_feelings'));

function remove(feeling: JournalFeelingOption): void {
  selected.value = selected.value.filter((chosen) => chosen.id !== feeling.id);
}
</script>

<template>
  <div class="jff-row">
    <span
      v-for="feeling in selected"
      :key="feeling.id"
      class="ef-feel-tag"
    >
      {{ feeling.name }}
      <button
        type="button"
        class="x"
        :aria-label="$t('journal_tile_remove_feeling', {feeling: feeling.name})"
        @click="remove(feeling)"
      ><svg
        width="8"
        height="8"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
        aria-hidden="true"
      ><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg></button>
    </span>

    <!-- A search box over a list of feelings, so the panel this opens is a dialog and not a menu.
         Quasar 2.28 stopped declaring role="menu" on menu content, which is what had made the
         earlier `aria-haspopup="menu"` true. -->
    <button
      type="button"
      class="jff-add"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      {{ $t(addLabel) }}
      <!-- Drawn rather than typed: a "+" as text is copy the i18n rules would want translated,
           and it is a glyph. The label beside it carries the meaning. -->
      <span class="jff-plus" aria-hidden="true"><svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
      ><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></span>

      <q-menu
        v-model="isOpen"
        class="jff-menu"
        role="dialog"
        :aria-label="$t('journal_tile_feelings_title')"
        :offset="[0, 8]"
        anchor="top middle"
        self="bottom middle"
        no-parent-event
      >
        <feelings-picker
          v-model="selected"
          v-model:search="search"
          class="jff-picker"
          :feelings-by-mood="feelingsByMood"
          :fallback-feelings="fallbackFeelings"
          :mood="mood"
          dismissible
          @dismiss="isOpen = false"
        />
      </q-menu>
    </button>
  </div>
</template>

<style scoped lang="scss">
.jff-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: var(--Spacing-spacing-3) var(--Spacing-spacing-4);
  border-radius: var(--card-border-radius);
  // `-0`, the same fill the note's textarea takes: the two are sibling fields on the card, and the
  // card itself is `-10`, so the row was invisible against it.
  background: var(--Neutral-UI-neutral-ui-0);
}

.jff-add {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 4px 0;
  border: 0;
  border-radius: 100px;
  background: none;
  cursor: pointer;
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-70);
  @include focus-visible;
}

.jff-plus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 100px;
  background: var(--Neutral-UI-neutral-ui-10);
  color: var(--Neutral-UI-neutral-ui-100);
  line-height: 1;
}

// Repeated from the picker because a chosen feeling is drawn in both places, and the picker's own
// copy is scoped to it.
.ef-feel-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  color: var(--Neutral-UI-neutral-ui-0);
  background: var(--Neutral-UI-neutral-ui-100);
  border-radius: 100px;
  padding: 6px 8px 6px 12px;
  text-transform: capitalize;

  .x {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    border: 0;
    border-radius: 100px;
    background: rgb(255 255 255 / 18%);
    color: inherit;
    cursor: pointer;
    line-height: 1;
    transition: background 150ms ease;

    &:hover {
      background: rgb(255 255 255 / 36%);
    }
  }
}
</style>

<style lang="scss">
// Unscoped: the menu is teleported out of this component, so a scoped rule never reaches it.
.jff-menu {
  border-radius: 16px;
  box-shadow: 0 8px 34px rgb(0 0 0 / 12%);
  background: var(--Neutral-UI-neutral-ui-0);
}

.jff-picker {
  padding: var(--Spacing-spacing-4);
  // Wide enough for four pills a row, as the design draws it, without pinning it so wide that a
  // phone has to scroll sideways.
  width: min(460px, calc(100vw - 32px));
}
</style>
